/**
 * UAT tracker rules shared by the Worker and the app (Rod, 2026-11-04).
 * Everything here is pure, so the Worker routes, the admin import preview
 * and the tests use the same statuses, limits and CSV parser.
 */

/**
 * UAT v2 statuses (Rod, 2026-11-04): Planned (with a target date), Shipped to
 * Your Environment, In Testing, Passed or Needs Fix. New is a customer item
 * that Prentus has not sorted yet, and Closed is a customer item that an
 * admin closed or merged. The database still accepts the old 'delivered',
 * which reads as 'shipped' (normalizeUatStatus).
 */
export const UAT_STATUSES = ['planned', 'new', 'shipped', 'in_testing', 'passed', 'needs_fix', 'closed'] as const
export type UatStatus = typeof UAT_STATUSES[number]

/** The statuses a customer can choose, and only while the item is in one of them. */
export const UAT_CUSTOMER_STATUSES = ['shipped', 'in_testing', 'passed', 'needs_fix'] as const satisfies readonly UatStatus[]
export type UatCustomerStatus = typeof UAT_CUSTOMER_STATUSES[number]

/** The statuses the ship sync changes: a Planned or New item ships, and a Needs Fix item ships again. */
export const UAT_SYNC_STATUSES = ['planned', 'new', 'needs_fix'] as const satisfies readonly UatStatus[]

export const UAT_CASE_RESULTS = ['not_run', 'pass', 'fail'] as const
export type UatCaseResult = typeof UAT_CASE_RESULTS[number]

export const UAT_STATUS_LABEL: Record<UatStatus, string> = {
  planned: 'Planned',
  new: 'New',
  shipped: 'Shipped to Your Environment',
  in_testing: 'In Testing',
  passed: 'Passed',
  needs_fix: 'Needs Fix',
  closed: 'Closed',
}

/** Short labels for status pills and filters in slim rows. */
export const UAT_STATUS_SHORT: Record<UatStatus, string> = {
  planned: 'Planned',
  new: 'New',
  shipped: 'Shipped',
  in_testing: 'In Testing',
  passed: 'Passed',
  needs_fix: 'Needs Fix',
  closed: 'Closed',
}

/** StatusBadge tones: neutral before it ships, info when it ships, pending while testing, success and danger for results. */
export const UAT_STATUS_TONE: Record<UatStatus, 'neutral' | 'info' | 'pending' | 'success' | 'danger'> = {
  planned: 'neutral',
  new: 'info',
  shipped: 'info',
  in_testing: 'pending',
  passed: 'success',
  needs_fix: 'danger',
  closed: 'neutral',
}

export const UAT_CASE_RESULT_LABEL: Record<UatCaseResult, string> = {
  not_run: 'Not Run',
  pass: 'Pass',
  fail: 'Fail',
}

/** What a customer adds with Add Item. */
export const UAT_REQUEST_TYPES = ['issue_found', 'question', 'request'] as const
export type UatRequestType = typeof UAT_REQUEST_TYPES[number]
export const UAT_REQUEST_TYPE_LABEL: Record<UatRequestType, string> = {
  issue_found: 'Issue Found',
  question: 'Question',
  request: 'Request',
}

/**
 * Item types (Rod, 2026-11-04, after DeVry's UAT analyst asked to tell
 * production escalations, release testing and roadmap items apart). Every
 * item has exactly one type. Existing items start as UAT.
 */
export const UAT_ITEM_TYPES = ['production', 'uat', 'roadmap'] as const
export type UatItemType = typeof UAT_ITEM_TYPES[number]
export const UAT_ITEM_TYPE_LABEL: Record<UatItemType, string> = {
  production: 'Production Issue',
  uat: 'UAT',
  roadmap: 'Roadmap',
}
export const UAT_ITEM_TYPE_HINT: Record<UatItemType, string> = {
  production: 'Something wrong in your live environment today',
  uat: 'Part of a release you are testing',
  roadmap: 'A request or future work, not ready to test',
}

export function isUatItemType(value: unknown): value is UatItemType {
  return typeof value === 'string' && (UAT_ITEM_TYPES as readonly string[]).includes(value)
}

/** A stored type as the app uses it. Unknown or missing values read as 'uat', the default. */
export function normalizeUatItemType(value: unknown): UatItemType {
  return isUatItemType(value) ? value : 'uat'
}

/**
 * The type a customer item starts with (Rod): Issue Found is UAT, Request is
 * Roadmap, and a Question takes the type of its project (the most common type
 * of the project's open items), else UAT. The customer can change it.
 */
export function defaultUatItemType(requestType: UatRequestType, projectItems: ReadonlyArray<{ itemType: UatItemType; status: UatStatus }> = []): UatItemType {
  if (requestType === 'issue_found') return 'uat'
  if (requestType === 'request') return 'roadmap'
  const counts: Record<UatItemType, number> = { production: 0, uat: 0, roadmap: 0 }
  for (const item of projectItems) if (item.status !== 'closed') counts[item.itemType] += 1
  const top = Math.max(counts.production, counts.uat, counts.roadmap)
  if (!top || counts.uat === top) return 'uat'
  return counts.production === top ? 'production' : 'roadmap'
}

/** Counts per type, in type order, for the type filter chips. */
export function uatTypeCounts(items: ReadonlyArray<{ itemType: UatItemType }>): Record<UatItemType, number> {
  const counts: Record<UatItemType, number> = { production: 0, uat: 0, roadmap: 0 }
  for (const item of items) if (isUatItemType(item.itemType)) counts[item.itemType] += 1
  return counts
}

/**
 * True when an item waits for the customer (the Test Now view): it shipped to
 * their environment or is In Testing, of any type, or it is an open
 * Production Issue (not Passed and not Closed).
 */
export function uatNeedsCustomerAction(item: { itemType: UatItemType; status: UatStatus }): boolean {
  if (item.status === 'shipped' || item.status === 'in_testing') return true
  return item.itemType === 'production' && item.status !== 'passed' && item.status !== 'closed'
}

export type UatOrder = 'test_now' | 'project'
export const UAT_ORDER_LABEL: Record<UatOrder, string> = { test_now: 'Test Now', project: 'Project Order' }

/**
 * How one project's items show (Rod, 2026-11-04). testNow: the items that wait
 * for the customer, open Production Issues first (Test Now order only).
 * others: the rest, in project order. roadmap: Roadmap items that do not wait
 * for the customer, collapsed in a "Roadmap (N)" group unless collapseRoadmap
 * is false (the customer filters by type). The input order is the admin's
 * project order and stays inside each part.
 */
export function uatProjectSections<T extends { itemType: UatItemType; status: UatStatus }>(items: readonly T[], options: { order: UatOrder; collapseRoadmap: boolean }): { testNow: T[]; others: T[]; roadmap: T[] } {
  const testNow: T[] = []
  const others: T[] = []
  const roadmap: T[] = []
  for (const item of items) {
    const action = uatNeedsCustomerAction(item)
    if (options.order === 'test_now' && action) testNow.push(item)
    else if (options.collapseRoadmap && item.itemType === 'roadmap' && !action) roadmap.push(item)
    else others.push(item)
  }
  // Open Production Issues lead Test Now. The sort is stable, so the project order stays within each type.
  testNow.sort((a, b) => Number(b.itemType === 'production') - Number(a.itemType === 'production'))
  return { testNow, others, roadmap }
}

/** Test Now: projects with items that wait for the customer come first. The sort is stable, so the project order stays. */
export function uatOrderProjects<T extends { items: ReadonlyArray<{ itemType: UatItemType; status: UatStatus }> }>(groups: readonly T[], order: UatOrder): T[] {
  if (order !== 'test_now') return [...groups]
  return [...groups].sort((a, b) => Number(b.items.some(uatNeedsCustomerAction)) - Number(a.items.some(uatNeedsCustomerAction)))
}

/** The project a customer item lands in when the customer picks no project. Made on first use. */
export const UAT_REQUESTS_PROJECT = { key: 'your-requests', name: 'Your Requests', description: 'Issues, questions and requests your team added.' } as const

/** Download visibility. until_shipped shows the file at the workspace's next ship, or when the chosen Linear issues ship. */
export const UAT_FILE_VISIBILITIES = ['visible', 'hidden', 'until_shipped'] as const
export type UatFileVisibility = typeof UAT_FILE_VISIBILITIES[number]
export const UAT_FILE_VISIBILITY_LABEL: Record<UatFileVisibility, string> = {
  visible: 'Visible',
  hidden: 'Hidden',
  until_shipped: 'Hidden Until Shipped',
}
export const UAT_FILE_GATES = ['next_ship', 'issues'] as const
export type UatFileGate = typeof UAT_FILE_GATES[number]

/** File types an admin can attach, by extension. The Worker stores and serves the type from this list, never from the browser. */
export const UAT_FILE_TYPES: Record<string, string> = {
  '.zip': 'application/zip',
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.csv': 'text/csv',
  '.txt': 'text/plain',
  '.mp4': 'video/mp4',
}

/** The stored type for a file name, or null when the extension is not allowed. */
export function uatFileType(name: string): string | null {
  const extension = name.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] || ''
  return UAT_FILE_TYPES[extension] || null
}

export const UAT_LIMITS = {
  titleChars: 200,
  descriptionChars: 2_000,
  noteChars: 2_000,
  caseChars: 1_000,
  areaChars: 120,
  itemsPerWorkspace: 200,
  casesPerItem: 500,
  /** Feature notes for the Prentus Assistant. */
  specChars: 20_000,
  /** The CSV text an admin can import at once. */
  csvChars: 500_000,
  projectNameChars: 120,
  projectDescriptionChars: 600,
  projectsPerWorkspace: 40,
  fileTitleChars: 160,
  fileNoteChars: 2_000,
  versionLabelChars: 40,
  filesPerWorkspace: 100,
  /** 25 MB, like other workspace files. */
  fileBytes: 25 * 1024 * 1024,
  /** Linear issues an admin can pick as a file's gate. */
  gateIssues: 10,
} as const

export const UAT_KEY = /^[a-z0-9][a-z0-9-]{0,79}$/
export const LINEAR_IDENTIFIER = /^[A-Z][A-Z0-9]{0,9}-[0-9]{1,7}$/

export function isUatStatus(value: unknown): value is UatStatus {
  return typeof value === 'string' && (UAT_STATUSES as readonly string[]).includes(value)
}

/** A stored status as the app uses it: the old 'delivered' is 'shipped'. Unknown values read as 'planned'. */
export function normalizeUatStatus(value: unknown): UatStatus {
  if (value === 'delivered') return 'shipped'
  return isUatStatus(value) ? value : 'planned'
}

export function isUatCustomerStatus(value: unknown): value is UatCustomerStatus {
  return typeof value === 'string' && (UAT_CUSTOMER_STATUSES as readonly string[]).includes(value)
}

export function isUatRequestType(value: unknown): value is UatRequestType {
  return typeof value === 'string' && (UAT_REQUEST_TYPES as readonly string[]).includes(value)
}

export function isUatFileVisibility(value: unknown): value is UatFileVisibility {
  return typeof value === 'string' && (UAT_FILE_VISIBILITIES as readonly string[]).includes(value)
}

/** "3/5 passed" for a slim row, or null when the item has no test cases. */
export function uatCaseTally(cases: ReadonlyArray<{ result: UatCaseResult }>): string | null {
  if (!cases.length) return null
  return `${cases.filter((entry) => entry.result === 'pass').length}/${cases.length} passed`
}

/** "6 of 9 passed" for a project header. Closed items and Roadmap items (not ready to test) do not count. */
export function uatProjectProgress(items: ReadonlyArray<{ status: UatStatus; itemType?: UatItemType }>): { passed: number; total: number; label: string } {
  const open = items.filter((item) => item.status !== 'closed' && item.itemType !== 'roadmap')
  const passed = open.filter((item) => item.status === 'passed').length
  // A project with only Roadmap items has items, but nothing to test yet.
  const empty = items.some((item) => item.status !== 'closed') ? 'Nothing to test yet' : 'No items yet'
  return { passed, total: open.length, label: open.length ? `${passed} of ${open.length} passed` : empty }
}

/** "692 KB" or "1.4 MB". */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`
}

export function isUatCaseResult(value: unknown): value is UatCaseResult {
  return typeof value === 'string' && (UAT_CASE_RESULTS as readonly string[]).includes(value)
}

/** Counts per status, in status order, for the summary at the top of the tab. */
export function uatStatusCounts(items: ReadonlyArray<{ status: UatStatus }>): Record<UatStatus, number> {
  const counts: Record<UatStatus, number> = { planned: 0, new: 0, shipped: 0, in_testing: 0, passed: 0, needs_fix: 0, closed: 0 }
  for (const item of items) if (isUatStatus(item.status)) counts[item.status] += 1
  return counts
}

/**
 * A Linear issue reference from what an admin pastes: an identifier
 * ("P-1234", any case) or an issue URL
 * ("https://linear.app/prentus/issue/P-1234/some-title"). The URL is kept
 * only when it is a linear.app issue URL.
 */
export function parseLinearIssueRef(input: string): { identifier: string; url: string | null } | null {
  const value = input.trim()
  if (!value) return null
  const plain = value.toUpperCase()
  if (LINEAR_IDENTIFIER.test(plain)) return { identifier: plain, url: null }
  let url: URL
  try { url = new URL(value) } catch { return null }
  if (url.protocol !== 'https:' || url.hostname !== 'linear.app') return null
  const match = /^\/[A-Za-z0-9_-]{1,80}\/issue\/([A-Za-z][A-Za-z0-9]{0,9}-[0-9]{1,7})(?:\/[^/?#]*)?\/?$/.exec(url.pathname)
  if (!match) return null
  const identifier = match[1].toUpperCase()
  return { identifier, url: `https://linear.app${url.pathname.replace(/\/$/, '')}` }
}

/** Trims text, normalizes line breaks, and drops control characters other than tab and line feed. */
export function normalizeUatText(value: string): string {
  return value
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, '')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim()
}

/** One line of text (titles, areas): no line breaks, single spaces. */
export function singleLine(value: string): string {
  return normalizeUatText(value).replace(/\s+/g, ' ')
}

/** Cuts text to max UTF-16 units without leaving half of an emoji, which the database refuses. */
export function cutText(value: string, max: number): string {
  const cut = value.slice(0, max)
  return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut
}

// --- CSV import ------------------------------------------------------------

export interface ParsedUatCase {
  text: string
  area: string | null
}

export interface UatCsvResult {
  cases: ParsedUatCase[]
  /** The header names used, or null when the file had no recognized header. */
  caseColumn: string | null
  areaColumn: string | null
  /** Rows without case text, and rows past the per-item limit. */
  skipped: number
  /** Set when nothing could be imported. Sentence case, for the admin. */
  error: string | null
}

/**
 * Header names, normalized to lower-case letters and digits. Earlier names win.
 * "name" and "section/column" match an Asana CSV export.
 */
const CASE_HEADERS = ['testcase', 'case', 'testcasename', 'casename', 'testcasedescription', 'casedescription', 'scenario', 'testscenario', 'scenarioname', 'teststeps', 'steps', 'step', 'test', 'testname', 'title', 'testtitle', 'requirement', 'description', 'summary', 'name', 'taskname', 'task']
const AREA_HEADERS = ['area', 'functionalarea', 'module', 'feature', 'featurearea', 'component', 'section', 'sectioncolumn', 'category', 'page', 'screen', 'epic']
const EXPECTED_HEADERS = ['expectedresult', 'expectedresults', 'expected', 'expectedoutcome', 'expectedbehavior', 'expectedbehaviour']

function normalizeHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function detectDelimiter(firstLine: string): string {
  const counts = [',', ';', '\t'].map((delimiter) => ({ delimiter, count: firstLine.split(delimiter).length - 1 }))
  counts.sort((a, b) => b.count - a.count)
  return counts[0].count > 0 ? counts[0].delimiter : ','
}

/** RFC 4180 rows: quoted fields, doubled quotes, line breaks inside quotes, CRLF or LF. */
export function parseCsvRows(text: string): string[][] {
  const source = text.replace(/^﻿/, '')
  const firstLine = source.split(/\r?\n/, 1)[0] || ''
  const delimiter = detectDelimiter(firstLine)
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  for (let index = 0; index < source.length; index += 1) {
    const character = source[index]
    if (quoted) {
      if (character === '"') {
        if (source[index + 1] === '"') { field += '"'; index += 1 }
        else quoted = false
      } else field += character
      continue
    }
    if (character === '"' && field.trim() === '') { quoted = true; field = ''; continue }
    if (character === delimiter) { row.push(field); field = ''; continue }
    if (character === '\n' || character === '\r') {
      if (character === '\r' && source[index + 1] === '\n') index += 1
      row.push(field)
      rows.push(row)
      row = []
      field = ''
      continue
    }
    field += character
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row) }
  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''))
}

function findColumn(headers: string[], names: string[], exclude: number[] = []): number {
  const normalized = headers.map(normalizeHeader)
  for (const name of names) {
    const index = normalized.findIndex((header, position) => header === name && !exclude.includes(position))
    if (index >= 0) return index
  }
  return -1
}

/**
 * Reads test cases from a CSV file. It looks for a case column (Case, Test
 * Case, Scenario, Description, Steps, Title, Name and similar) and an
 * optional area column (Area, Module, Feature, Section and similar). An
 * Expected Result column is added to the case text. A file with one column
 * and no recognized header is read as one case per line.
 */
export function parseUatCasesCsv(text: string, limit: number = UAT_LIMITS.casesPerItem): UatCsvResult {
  const empty = (error: string): UatCsvResult => ({ cases: [], caseColumn: null, areaColumn: null, skipped: 0, error })
  if (!text.trim()) return empty('The file is empty.')
  if (text.length > UAT_LIMITS.csvChars) return empty('The file is too large. Import at most 500,000 characters at a time.')
  const rows = parseCsvRows(text)
  if (!rows.length) return empty('The file has no rows.')

  const headers = rows[0].map((cell) => cell.trim())
  let caseIndex = findColumn(headers, CASE_HEADERS)
  let body = rows.slice(1)
  let caseColumn: string | null = caseIndex >= 0 ? headers[caseIndex] : null
  if (caseIndex < 0) {
    const width = Math.max(...rows.map((cells) => cells.length))
    if (width === 1) {
      // One column and no header: every line is a case.
      caseIndex = 0
      body = rows
    } else {
      return empty('No case column was found. Name one column Case, Test Case, Scenario or Description.')
    }
  }
  const areaIndex = caseColumn ? findColumn(headers, AREA_HEADERS, [caseIndex]) : -1
  const expectedIndex = caseColumn ? findColumn(headers, EXPECTED_HEADERS, [caseIndex, areaIndex]) : -1

  const cases: ParsedUatCase[] = []
  let skipped = 0
  for (const cells of body) {
    const caseText = normalizeUatText(cells[caseIndex] || '')
    if (!caseText) { skipped += 1; continue }
    if (cases.length >= limit) { skipped += 1; continue }
    const expected = expectedIndex >= 0 ? normalizeUatText(cells[expectedIndex] || '') : ''
    const full = expected ? `${caseText}\nExpected: ${expected}` : caseText
    const area = areaIndex >= 0 ? cutText(singleLine(cells[areaIndex] || ''), UAT_LIMITS.areaChars) : ''
    cases.push({ text: cutText(full, UAT_LIMITS.caseChars), area: area || null })
  }
  if (!cases.length) return { ...empty('No rows had case text.'), caseColumn, skipped }
  return {
    cases,
    caseColumn,
    areaColumn: areaIndex >= 0 ? headers[areaIndex] : null,
    skipped,
    error: null,
  }
}
